import {
  flattenSupersessionsAsParts,
  SearchablePart,
} from '@components/Product/Desktop/Panels/PartsCatalogue/Autocomplete';
import filterOptionsFunction, { uniqueKey } from '@components/Product/Shared/Helpers/filterSearchOptions';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete as MUIAutocomplete,
  AutocompleteRenderInputParams,
  InputAdornment,
  styled,
  TextField,
} from '@mui/material';
import { useTenant } from '@providers/Tenant';
import { makeKitUrl } from '@services/kits/kitUrl';
import { kitsToPartPaths } from '@services/kits/utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SearchOption from 'src/components/Product/Mobile/Panels/PartsCatalogue/Autocomplete/SearchOption';
import {
  FullScreenPopper,
  StyledPopper,
} from 'src/components/Product/Mobile/Panels/PartsCatalogue/Autocomplete/styles';
import { useProduct } from 'src/providers/Product';
import { makePartUrl } from 'src/services/partUrl';
import i18n from '../../../../../../i18n';

const MINIMUM_SEARCH_TERM_LENGTH = 3;
const INITIAL_SEARCH_TERM = '';
export const CHARACTERS_TO_ESCAPE_REGEX = /[.*+?^${}()|[\]\\]/g;

interface AutocompleteProps {
  fullscreen: boolean;
  closeSearch: () => void;
}

export function Autocomplete({ fullscreen, closeSearch }: Readonly<AutocompleteProps>): JSX.Element {
  const [query, setQuery] = useState(INITIAL_SEARCH_TERM);
  const history = useHistory();
  const { productId, partPaths, kits } = useProduct();
  const { enabledFeatures } = useTenant();
  const { t } = useTranslation('partsCatalogue');

  const enabled = query.length >= MINIMUM_SEARCH_TERM_LENGTH;
  const escapedQuery = query.replace(CHARACTERS_TO_ESCAPE_REGEX, '\\$&');
  const regex = new RegExp(escapedQuery, 'gi');
  /* istanbul ignore next */
  const popperComponent = fullscreen ? FullScreenPopper : StyledPopper;

  const searchOptions = useMemo(() => {
    const options = [...flattenSupersessionsAsParts(partPaths)];

    if (enabledFeatures.includes('kits')) {
      options.push(...kitsToPartPaths(kits));
    }

    return options;
  }, [partPaths, kits, enabledFeatures]);

  const handleOptionChange = useCallback(
    (_event: React.SyntheticEvent, option: SearchablePart) => {
      const {
        part: { id, focal, kits },
        pathHash,
        partNumber,
      } = option;
      if (kits) {
        history.push(makeKitUrl({ productId, externalId: id }));
      } else {
        history.push(
          makePartUrl({
            productId,
            partId: id,
            params: partNumber
              ? /* istanbul ignore next */ {
                  group: pathHash,
                  supersession: partNumber,
                  linkedProductId: focal?.linkedProductId,
                }
              : { group: pathHash, linkedProductId: focal?.linkedProductId },
          })
        );
      }
    },
    [history, productId]
  );

  return (
    <MUIAutocomplete<SearchablePart, false, true, false> // <T (Type of options), Multiple, DisableClearable, FreeSolo>
      data-testid="bom-filter-input"
      disableClearable
      autoHighlight
      fullWidth
      size="small"
      getOptionLabel={(option) => option.part.name}
      isOptionEqualToValue={(option: SearchablePart, value: SearchablePart) => uniqueKey(option) === uniqueKey(value)} // Fixes console warnings, probably some reference field breaks === comparison
      filterOptions={filterOptionsFunction(regex, enabled)} // Filter function closes over regex
      options={searchOptions} // Adding supersessions here to parts taken from product provider
      noOptionsText={enabled ? t('nothingFound') : t('pleaseEnterMoreCharacters', { min: MINIMUM_SEARCH_TERM_LENGTH })}
      onChange={handleOptionChange}
      onClick={/* istanbul ignore next */ (e) => e.stopPropagation()}
      onInputChange={(_, value) => setQuery(value)} // Connect Autocomplete value with react state
      renderInput={renderInputWrapper(closeSearch)}
      PopperComponent={popperComponent} // Popper required to target the list item
      renderOption={(props, option) => (
        <SearchOption
          key={uniqueKey(option)}
          props={props}
          searchablePart={option}
          regex={regex}
          showKitChip={Boolean(option.part.kits)}
        />
      )}
      id="search-input"
    />
  );
}

const renderInputWrapper = (closeSearch: () => void) =>
  function renderInput(params: AutocompleteRenderInputParams) {
    const { t } = i18n;
    return (
      <StyledTextField
        {...params}
        focused
        margin="normal"
        onClick={(e) => e.stopPropagation()}
        placeholder={t('partsCatalogue:searchForPartNameOrNumber')}
        InputProps={{
          ...params.InputProps,
          className: 'override-class',
          autoFocus: true,
          startAdornment: <SearchIcon onClick={closeSearch} />,
        }}
      />
    );
  };

const StyledTextField = styled(TextField)`
  width: 100%;
  margin: 0 !important;
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border-bottom: 1px solid hsla(0, 0%, 100%, 0.56);
  border-radius: 0;

  input::placeholder {
    opacity: 1;
    font-style: italic;
  }

  .override-class .MuiAutocomplete-endAdornment {
    display: none !important;
  }

  .override-class .MuiAutocomplete-input {
    color: ${({ theme }) => theme.palette.secondary.main} !important;
  }

  .override-class .MuiAutocomplete-input::placeholder {
    color: hsl(0, 0%, 100%) !important;
  }
`;

function SearchIcon({ onClick }: any) {
  return (
    <InputAdornment position="start" onClick={onClick}>
      <WhiteIcon icon={faMagnifyingGlass} size="lg" />
    </InputAdornment>
  );
}

const WhiteIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
  color: ${({ theme }) => theme.palette.sidebarBackground.contrastText};
`;
